<template>
    <div>
        <v-inline-loader v-if="isLoading"/>
        <v-select
            class="stack-m"
            label="Filter by date"
            placeholder="Select date"
            @option-select="handleFilterDate"
            :value="selectedDate"
            :options="versionsByDateOptions"/>
        <div class="divider divider-tile-content"></div>
        <p v-if="emptyResults" class="text-center text-muted">{{ emptyMessage }}</p>
        <div :style="[enableItemStyles ? {height: '300px'} : {height: 'auto'}]"
             :class="{'scroll-y': enableItemStyles}">
            <ul class="list list-group list-hoverable list-relaxed">
                <li v-for="item in items"
                    class="list-item align-items-center cursor-pointer"
                    :class="{'is-active': version === item.version}"
                    :data-version="item.version"
                    @click="handleNavigateToVersion">
                    <span>{{ item[dateFilterKeyName] | dateTime}}</span>
                    <r-badge class="no-flex">Version {{ item.version }}</r-badge>
                </li>
            </ul>
        </div>
        <template v-if="enablePreviousButton || enableNextButton">
            <div class="divider divider-tile-content"></div>
            <div class="grid-fitted">
                <div class="grid-item">
                    <v-icon-button
                        v-if="enablePreviousButton"
                        size="small"
                        @click="handleGoPreviousPage">
                        <v-icon slot="left-icon" icon="arrow-left"></v-icon>
                        Previous
                    </v-icon-button>
                </div>
                <div class="grid-item text-right">
                    <v-icon-button
                        v-if="enableNextButton"
                        size="small"
                        @click="handleGoNextPage"
                        class="float-right">
                        Next
                        <v-icon slot="right-icon" icon="arrow-right"></v-icon>
                    </v-icon-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import vInlineLoader from '@/components/v-inline-loader';
    import {
        vDateInput, vIcon, vIconButton, vSelect,
    } from '@/components/form';
    import {DateTimeFormats} from '@/common/datetime-formats';

    export default {
        name: 'versions-list-filterParams',
        components: {
            vDateInput,
            vIcon,
            vIconButton,
            vSelect,
            vInlineLoader,
        },
        props: {
            dateFilterKeyName: {
                type: String,
                required: false,
                default: 'updatedTime',
            },
            emptyMessage: {
                type: String,
                required: false,
                default: 'No items exist at this date',
            },
            isLoading: {
                type: Boolean,
                required: true,
            },
            version: {
                type: [String, Number],
                required: true,
            },
            filterParams: {
                type: Object,
                required: true,
            },
            items: {
                type: Array,
                required: true,
            },
        },
        computed: {
            emptyResults() {
                return this.items.length === 0;
            },
            enablePreviousButton() {
                return this.items.length > 0 && this.items.length === this.filterParams.limit;
            },
            enableNextButton() {
                return this.filterParams.offset > 0;
            },
            enableItemStyles() {
                return this.items.length > 6;
            },
            selectedDate() {
                // filter date period value date..date
                const dateFilterPeriod = (this.filterParams && this.filterParams.filter)
                    ? this.filterParams.filter[this.dateFilterKeyName]
                    : undefined;

                // filter date value, first section from date..date
                const filterDateValue = dateFilterPeriod ? dateFilterPeriod.split('..').shift() : undefined;

                // returns formatted value
                return filterDateValue
                    ? this.$tz().readUTC(filterDateValue).startOf('day').format(DateTimeFormats.longDate)
                    : '';
            },
            versionsByDateOptions() {
                const uniqDates = [
                    ...new Set(this.items
                        .map(item => this.$tz().readUTC(item[this.dateFilterKeyName]).startOf('day').format(DateTimeFormats.longDate))),
                ];
                return [
                    {
                        label: 'All dates',
                        value: '',
                    },
                    ...uniqDates.map(createdDate => ({
                        label: createdDate,
                        value: createdDate,
                    })),
                ];
            },
        },
        methods: {
            handleNavigateToVersion(e) {
                const {version} = e.currentTarget.dataset;
                this.$emit('navigate', {version});
            },
            handleGoPreviousPage() {
                this.$emit('filter', ({
                    ...this.filterParams,
                    offset: this.filterParams.offset + this.filterParams.limit,
                }));
            },
            handleGoNextPage() {
                this.$emit('filter', ({
                    ...this.filterParams,
                    offset: this.filterParams.offset - this.filterParams.limit,
                }));
            },
            handleFilterDate({value}) {
                // clear date value
                if (!value) {
                    this.$emit('filter', ({
                        ...this.filterParams,
                        offset: 0,
                        filter: {},
                    }));
                    return;
                }

                // filter by date
                const day = this.$tz().readUTCFormat(value, DateTimeFormats.longDate);
                const dateFilterValue = `${day.format()}..${day.endOf('day').format()}`;
                this.$emit('filter', ({
                    ...this.filterParams,
                    offset: 0,
                    filter: {[this.dateFilterKeyName]: dateFilterValue},
                }));
            },
        },
    };

</script>
